"use client";
import React, {
  useEffect,
  useMemo,
  useRef,
  Dispatch,
  SetStateAction,
  useState,
} from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { styled } from "@mui/material/styles";
import { blueGrey, red } from "@mui/material/colors";
import { GlobalStyles } from "@mui/material";

const StyledInput = styled("input")<{ $error?: boolean }>(({ $error }) => ({
  backgroundColor: blueGrey[50],
  border: `1px solid ${$error ? red[500] : "grey"}`,
  borderRadius: "4px",
  height: "30px",
  width: 350,
  padding: "0 8px",
  outline: "none",
}));

type Props = {
  onSelect?: (place: google.maps.places.PlaceResult) => void; // fires only on real selection
  address: string;
  city: string;
  state: string;
  zip: string;
  disabled: boolean;
  requireSelectionMessage?: string; // custom error text
  addressErr: boolean;
  setAddressErr: Dispatch<SetStateAction<boolean>>;
};

export default function AddressInput({
  onSelect,
  address,
  city,
  state,
  zip,
  disabled,
  requireSelectionMessage = "Please select an address from the suggestions.",
  addressErr,
  setAddressErr,
}: Props) {
  const apiKey = "AIzaSyA88wft0E_wVqceDTotka6Xxv9RZkp7uYE";
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  const lastPlaceRef = useRef<google.maps.places.PlaceResult | null>(null);

  // Build combined address only if ALL fields are non-empty
  const { hasAll, combined } = useMemo(() => {
    const parts = [address, city, state, zip].map((s) => (s ?? "").trim());
    const allFilled = parts.every((s) => s.length > 0);
    return {
      hasAll: allFilled,
      combined: allFilled
        ? `${parts[0]}, ${parts[1]}, ${parts[2]} ${parts[3]}`
        : "",
    };
  }, [address, city, state, zip]);

  useEffect(() => {
    let listener: google.maps.MapsEventListener | null = null;
    let cancelled = false;

    (async () => {
      const loader = new Loader({
        apiKey,
        version: "weekly",
        libraries: ["places"],
      });
      await loader.load();
      if (cancelled || !inputRef.current) {
        setAddressErr(true);
        return;
      }

      acRef.current = new google.maps.places.Autocomplete(inputRef.current, {
        fields: ["place_id", "formatted_address"],
        types: ["address"],
        componentRestrictions: { country: "us" },
      });

      listener = acRef.current.addListener("place_changed", () => {
        const place = acRef.current?.getPlace();
        if (!place) return;
        lastPlaceRef.current = place;
        setAddressErr(false);
        if (place.formatted_address && inputRef.current) {
          inputRef.current.value = place.formatted_address; // reflect selected value
        }
        onSelect?.(place); // ✅ only fires on true selection
      });
    })();

    return () => {
      setAddressErr(false);
      cancelled = true;
      listener?.remove?.();
      acRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill once if all props present and user hasn't typed yet
  useEffect(() => {
    const input = inputRef.current;
    if (!input || !hasAll) return;
    if (input.value.trim() === "") input.value = combined;
  }, [hasAll, combined]);

  function handleChange() {
    if (
      !lastPlaceRef.current?.place_id ||
      inputRef.current?.value !== lastPlaceRef.current?.formatted_address
    ) {
      setAddressErr(true);
    } else {
      setAddressErr(false);
    }
  }
  
  return (
    <>
      {/* Keep PAC dropdown above modals/overlays */}
      <GlobalStyles
        styles={(theme) => ({
          ".pac-container": { zIndex: (theme.zIndex?.modal ?? 1300) + 10 },
          ".input-helper": { color: red[600], fontSize: 12, marginTop: 4 },
        })}
      />
      <div style={{ display: "grid", gap: 6, width: 350 }}>
        <StyledInput
          ref={inputRef}
          type="text"
          disabled={disabled}
          placeholder="Start typing an address"
          autoComplete="off"
          defaultValue={hasAll ? combined : ""}
          onChange={handleChange}
          // onBlur={requireSelection}
          // onKeyDown={(e) => {
          //   if (e.key === "Enter") {
          //     requireSelection(e); // block form submit if not selected
          //   }
          // }}
          $error={addressErr}
          aria-invalid={addressErr || undefined}
        />
        {addressErr && (
          <span className="input-helper">{requireSelectionMessage}</span>
        )}
      </div>
    </>
  );
}
